<template>
  <div class="cardVerificationDetails">
    <el-dialog
      :title="$t('data.card_verification_details')"
      class="diglog-card"
      :visible.sync="dialogVisible"
      width="580px"
      :close-on-click-modal="false"
    >
      <div class="header-line" />
      <div class="total">
        <div class="total-item">
          <div class="total-title">{{ $t('data.card_verification_fee') }}:</div>
          <div class="total-num">{{ detailData.validation_currency || '-' }} {{ detailData.fee_split | toDivideWithDot }}</div>
        </div>
      </div>

      <div class="section-title">
        {{ $t('data.timeline') }}
      </div>
      <div class="line-bottom-1px" />
      <div class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.create_time') }}</span>
        <span class="content-blue">{{ $t('merchantAccount.complete_time') }}</span>
      </div>
      <div class="item">
        <span class="item-left content-gray">{{ detailData.transaction_create_time || '-' }}</span>
        <span class="content-gray">{{ detailData.transaction_return_time || '-' }}</span>
      </div>

      <div class="section-title">
        {{ $t('data.description') }}
      </div>
      <div class="line-bottom-1px" />

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.transaction_id') }}</span>
        <span class="content-gray">{{ detailData.transaction_id || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.merchant_order_id') }}</span>
        <span class="content-gray">{{ detailData.merchant_order_id || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.recipient_name') }}</span>
        <span class="content-gray">{{ detailData.account_name || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.bank_wallet_name') }}</span>
        <span class="content-gray">{{ detailData.account_bank_code || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.account_no') }}</span>
        <span class="content-gray">{{ detailData.account_no || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.account_status') }}</span>
        <span class="content-gray">{{ getAccountStatusLabel(detailData.account_validate_result) || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.account_actual_type') }}</span>
        <span class="content-gray">{{ getAccountTypeLabel(detailData.real_account_type) || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.name_match_result') }}</span>
        <span class="content-gray">{{ getNameMatchResultLabel(detailData.name_match_result) || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.cardholder_real_name') }}</span>
        <span class="content-gray">{{ detailData.real_account_name || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.account_kyc_result') }}</span>
        <span class="content-gray">{{ getAccountKycResultLabel(detailData.account_kyc_result) || '-' }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.failure_message') }}</span>
        <span class="content-gray">{{ detailData.failed_message || '-' }}</span>
      </div>

      <el-tag :type="getTransactionStatusType(detailData.transaction_status)" class="tag-custom payin-status">{{ getTransactionStatusLabel(detailData.transaction_status) }}</el-tag>
    </el-dialog>
  </div>
</template>

<script>
import { fetchCardValidationDetail, fetchCardValidationDetailForMg } from '@/api/data/data'
import {
  accountNameMatchResultMap,
  accountTypeMap,
  accountValidateResultMap,
  accountKycResultMap,
  cardValidationStatusMap
} from '@/utils/maps'

export default {
  name: 'CardVerificationDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      detailLoading: false,
      detailData: {}
    }
  },
  computed: {
    getAccountTypeLabel() {
      return function(value) {
        return accountTypeMap(value)?.label || '-'
      }
    },
    getNameMatchResultLabel() {
      return function(value) {
        return accountNameMatchResultMap(value)?.label || '-'
      }
    },
    getAccountStatusLabel() {
      return function(value) {
        return accountValidateResultMap(value)?.label || '-'
      }
    },
    getTransactionStatusType() {
      return function(value) {
        return cardValidationStatusMap(value)?.type || 'info'
      }
    },
    getTransactionStatusLabel() {
      return function(value) {
        return cardValidationStatusMap(value)?.label || '-'
      }
    },
    getAccountKycResultLabel() {
      return function(value) {
        return accountKycResultMap(value)?.label || '-'
      }
    },
    isFailureStatus() {
      return this.detailData.transaction_status === 2
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    changModalStatus(visible, transactionId) {
      this.dialogVisible = visible
      if (visible && transactionId) {
        this.getDetail(transactionId)
      }
    },
    handleDialogClose() {
      this.detailData = {}
    },
    getDetailInfo() {
      if (!this.detailData.transaction_id) return
      this.getDetail(this.detailData.transaction_id)
    },
    getDetail(transactionId) {
      this.detailLoading = true
      const isMgRoute = this.$route.path.includes('customerServiceManage')
      const apiFn = isMgRoute ? fetchCardValidationDetailForMg : fetchCardValidationDetail
      apiFn({ transaction_id: transactionId }).then(res => {
        if (res.code === 10000) {
          this.detailData = res.data || {}
        }
      }).finally(() => {
        this.detailLoading = false
      })
    }
  }
}
</script>

<style>
.cardVerificationDetails .el-dialog__title {
  font-weight: 500;
  font-family: 'Inter-Medium';
  font-size: 16px;
  color: #000000;
}
.cardVerificationDetails .header-line {
  margin: -26px 0 0;
  border-bottom: 1px solid #D3D7DF;
}
</style>

<style scoped lang="scss">
.total {
  display: inline-flex;
  justify-content: space-between;
  margin-top: 18px;
  min-width: 300px;

  .line {
    display: inline-block;
    content: '';
    height: 52px;
    width: 1px;
    background: #D3D7DF;
  }
  &-title {
    font-size: 14px;
    line-height: 17px;
    color: #606266;
  }
  &-num {
    margin-top: 12px;
    font-size: 22px;
    font-family: Source Han Sans SC;
    font-weight: 600;
    font-family: 'Inter-SemiBold';
    line-height: 1.2;
    color: #3855F1;
  }
}
.item {
  display: flex;
  margin-top: 16px;
  .item-left {
    display: inline-block;
    width: 250px;
  }
}

.content-blue {
  font-weight: 500;
  font-family: 'Inter-Medium';
  font-size: 14px;
  line-height: 17px;
  color: #1D3A71;
}
.content-gray {
  font-weight: 500;
  font-family: 'Inter-Medium';
  font-size: 14px;
  line-height: 15px;
  color: #4B5567;
}

.cardVerificationDetails {
  .payin-status {
    text-align: center;
    position: absolute;
    top: 20px;
    left: 240px;
  }
}
</style>
